import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const CMSPortalPage = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const cmsSections = [
    {
      id: 'overview',
      title: 'Overview',
      description: 'Dashboard and quick stats',
      icon: '📊',
      path: '/app/admin/cms/overview'
    },
    {
      id: 'events',
      title: 'Events Management',
      description: 'Manage society events and activities',
      icon: '📅',
      path: '/app/admin/cms/events'
    },
    {
      id: 'notifications',
      title: 'Notifications',
      description: 'Create and manage public notifications',
      icon: '🔔',
      path: '/app/admin/cms/notifications'
    },
    {
      id: 'press-releases',
      title: 'Press Releases',
      description: 'Manage official announcements',
      icon: '📰',
      path: '/app/admin/cms/press-releases'
    },
    {
      id: 'business',
      title: 'Business Directory',
      description: 'Manage business listings',
      icon: '🏪',
      path: '/app/admin/cms/business'
    },
    {
      id: 'gallery',
      title: 'Photo Gallery',
      description: 'Upload and manage photos',
      icon: '📸',
      path: '/app/admin/cms/gallery'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Content Management System</h1>
          <p className="mt-2 text-lg text-gray-600">
            Manage all public-facing content for your RWA website
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                  <span className="text-blue-600 text-lg">📅</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Upcoming Events</p>
                <p className="text-2xl font-semibold text-gray-900">12</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                  <span className="text-green-600 text-lg">🔔</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active Notifications</p>
                <p className="text-2xl font-semibold text-gray-900">8</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                  <span className="text-purple-600 text-lg">📰</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Published Articles</p>
                <p className="text-2xl font-semibold text-gray-900">24</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-md flex items-center justify-center">
                  <span className="text-yellow-600 text-lg">📸</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Gallery Photos</p>
                <p className="text-2xl font-semibold text-gray-900">156</p>
              </div>
            </div>
          </div>
        </div>

        {/* CMS Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cmsSections.map((section) => (
            <Link
              key={section.id}
              to={section.path}
              className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200 p-6 group"
            >
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors duration-200">
                    <span className="text-2xl">{section.icon}</span>
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                    {section.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {section.description}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Recent Activity */}
        <div className="mt-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Recent Activity</h2>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Latest Changes</h3>
            </div>
            <div className="divide-y divide-gray-200">
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-sm">+</span>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">New event added</p>
                      <p className="text-sm text-gray-500">Monthly Society Meeting - Dec 15, 2024</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">2 hours ago</span>
                </div>
              </div>
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 text-sm">📝</span>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Notification updated</p>
                      <p className="text-sm text-gray-500">Power Maintenance Notice</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">4 hours ago</span>
                </div>
              </div>
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-purple-600 text-sm">📰</span>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Press release published</p>
                      <p className="text-sm text-gray-500">Digital Transformation Initiative</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">1 day ago</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CMSPortalPage;
